<template>
	<div v-bind="$attrs" class="flex flex-col h-max min-w-0 w-full">
		<!-- Toolbar -->
		<div v-if="editor && !readOnly" class="toolbar flex flex-wrap gap-2 sm:gap-3 p-2 items-center border-b">
			<!-- Undo/Redo buttons -->
			<UndoRedoComponent :editor="editor" />

			<!-- Vertical divider -->
			<div class="w-px h-6 bg-gray-300 shrink-0" />

			<!-- Inline text formatting buttons -->
			<BoldButton :editor="editor" />
			<ItalicButton :editor="editor" />
			<UnderlineButton :editor="editor" />
			<TextColorButton
				:editor="editor"
				:isSmallScreen="isSmallScreen"
				:activeColor="activeColor"
				:colors="colors"
				@update:activeColor="activeColor = $event"
			/>
			<HighlightButton
				:editor="editor"
				:isSmallScreen="isSmallScreen"
				:activeHighlight="activeHighlight"
				:highlightColors="highlightColors"
			/>

			<!-- Vertical divider -->
			<div class="w-px h-6 bg-gray-300 shrink-0" />

			<!-- Link button (visible on sm and up) -->
			<LinkButton
				v-if="!isSmallScreen"
				ref="linkDropdown"
				:editor="editor"
				:isSmallScreen="isSmallScreen"
				:isActive="activeFormats.link"
				:linkTitle="linkTitle"
				:linkUrl="linkUrl"
				:linkMode="linkMode"
				:linkInfoHref="linkInfoHref"
				@open="onLinkDropdownOpen"
				@close="onLinkDropdownClose"
				@openDialog="openLinkDialog"
				@submit="submitLink"
				@cancel="cancelLink"
				@openExternally="openLinkExternally"
				@update:linkTitle="(v) => (linkTitle = v)"
				@update:linkUrl="(v) => (linkUrl = v)"
				@removeLink="unsetLinkFromToolbar"
			/>

			<!-- Block formatting buttons -->
			<BulletListButton v-if="!isSmallScreen" :editor="editor" />
			<NumberedListButton v-if="!isSmallScreen" :editor="editor" />

			<!-- More menu with link/unlink/bulletlist/numberedlist buttons => only smaller screens -->
			<MoreActionsMenu
				v-if="isSmallScreen"
				ref="moreMenuRef"
				:editor="editor"
				:isBulletActive="activeFormats.bulletList"
				:isOrderedActive="activeFormats.orderedList"
				@openExternally="openLinkExternally"
			/>
		</div>

		<!-- Editor (scrollable area so toolbar stays visible) -->
		<div class="editor-scroll flex-1 overflow-y-auto">
			<editor-content
				:editor="editor"
				:class="readOnly ? 'bg-transparent' : 'bg-base-100'"
				class="editor-content text-sm px-4 py-2 [&_.ProseMirror]:outline-none [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:list-outside [&_.ProseMirror_ul]:pl-6 [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:list-outside [&_.ProseMirror_ol]:pl-6 [&_.ProseMirror_ul_ul]:pl-10 [&_.ProseMirror_ol_ol]:pl-10 [&_.ProseMirror_li]:list-item"
			/>
		</div>
	</div>
</template>

<style>
/* some styles are in global style-prosemirror css file */
.toolbar {
	/* Better horizontal scroll on touch devices */
	-webkit-overflow-scrolling: touch;
	scrollbar-gutter: stable;
	/* Optional snapping for nicer paging across buttons */
	scroll-snap-type: x proximity;
}
.toolbar > * {
	scroll-snap-align: start;
}

.editor-scroll {
	/* Let the editor fill the available vertical space inside its parent */
	flex: 1 1 auto;
	min-height: 0; /* allow proper flexbox clipping */
	overflow-y: auto;
	/* Optional: nice scrolling */
}
.editor-content {
	min-height: 100%;
	height: 100%;
}
.editor-content:focus {
	outline: none;
}
.editor-content .ProseMirror {
	outline: none !important;
	min-height: 100%;
	height: 100%;
	padding: 0;
	margin: 0;
	box-sizing: border-box;
	/* Prevent long words/URLs from expanding the editor width */
	word-break: break-word; /* modern */
	overflow-wrap: anywhere; /* allow breaking long unbroken strings */
	white-space: pre-wrap; /* keep user line breaks but allow wrapping */
}
.editor-content .ProseMirror:focus {
	outline: none !important;
	border: none !important;
	box-shadow: none !important;
}
.editor-content .ProseMirror .is-editor-empty:first-child::before {
	content: attr(data-placeholder);
	color: rgb(156 163 175); /* text-gray-400 */
	pointer-events: none;
	height: 0;
	float: left; /* ensure caret starts at the very beginning */
	white-space: nowrap; /* keep placeholder on one line */
}
.editor-content {
	/* Avoid horizontal growth and accidental scrollbars */
	overflow-x: hidden;
}
</style>

<script lang="ts">
import { defineComponent, nextTick } from "vue";
import { getMarkRange } from "@tiptap/core";
import { moveCaretOutOfLink, stripProtocol, normalizeUrl } from "@/components/ui/texteditor/utils/linkUtils";
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { CustomLink } from "@/components/ui/texteditor/extensions/CustomLink";
import { Placeholder } from "@tiptap/extensions";

// component imports
import BoldButton from "@/components/ui/texteditor/BoldButton.vue";
import ItalicButton from "@/components/ui/texteditor/ItalicButton.vue";
import UnderlineButton from "@/components/ui/texteditor/UnderlineButton.vue";
import TextColorButton from "@/components/ui/texteditor/TextColorButton.vue";
import HighlightButton from "@/components/ui/texteditor/HighlightButton.vue";
import LinkButton from "@/components/ui/texteditor/LinkButton.vue";
import BulletListButton from "@/components/ui/texteditor/BulletListButton.vue";
import NumberedListButton from "@/components/ui/texteditor/NumberedListButton.vue";
import UndoRedoComponent from "@/components/ui/texteditor/UndoRedoComponent.vue";
import MoreActionsMenu from "@/components/ui/texteditor/MoreActionsMenu.vue";
import { colors, highlightColors } from "@/components/ui/texteditor/utils/palette";

export default defineComponent({
	name: "TextEditor",
	inheritAttrs: false,
	components: {
		EditorContent,
		BoldButton,
		ItalicButton,
		UnderlineButton,
		TextColorButton,
		HighlightButton,
		LinkButton,
		BulletListButton,
		NumberedListButton,
		UndoRedoComponent,
		MoreActionsMenu,
	},
	props: {
		modelValue: { type: String, default: "" },
		placeholder: { type: String, default: "" },
		enableClickSelection: { type: Boolean, default: true },
		readOnly: { type: Boolean, default: false },
	},
	emits: ["update:modelValue"],
	data() {
		return {
			editor: undefined as any,
			activeFormats: {
				bold: false,
				italic: false,
				underline: false,
				bulletList: false,
				orderedList: false,
				link: false,
			},
			activeColor: "#000000" as string,
			activeHighlight: null as string | null,
			linkTitle: "" as string,
			linkUrl: "" as string,
			isLinkDropdownOpen: false as boolean,
			resizingUI: false as boolean,
			linkMode: "create" as "create" | "edit",
			linkInfoHref: "" as string,
			pendingSelection: null as { from: number; to: number } | null,
			isSmallScreen: false as boolean,
		};
	},
	computed: {
		colors() {
			return colors;
		},
		highlightColors() {
			return highlightColors;
		},
	},
	methods: {
		unsetLinkFromToolbar() {
			const ed = this.editor as any;
			if (!ed) return;
			const { from, to } = ed.state.selection;
			if (from === to) {
				ed.chain().focus().extendMarkRange("link").unsetLink().run();
			} else {
				ed.chain().focus().unsetLink().run();
			}
		},
		// Accepts either a Tiptap JSON string, raw HTML, or plain text; returns suitable initial content.
		// Ensures there's at least one empty paragraph so the editor is focusable and placeholder shows.
		parseModelValue(value: string | null | undefined): any {
			const defaultDoc = { type: "doc", content: [{ type: "paragraph" }] } as any;
			if (value == null || value === "") return defaultDoc;
			try {
				const parsed = JSON.parse(value);
				if (parsed && typeof parsed === "object") {
					const hasContent = Array.isArray((parsed as any).content) && (parsed as any).content.length > 0;
					return hasContent ? parsed : defaultDoc;
				}
				return defaultDoc;
			} catch {
				// Not JSON; check if it's plain text (no HTML tags)
				if (!/<[a-z][\s\S]*>/i.test(value)) {
					// Convert newlines to paragraphs
					const lines = value.split(/\r?\n/);
					const content = lines.map((line) => ({
						type: "paragraph",
						content: line ? [{ type: "text", text: line }] : [],
					}));
					return { type: "doc", content: content.length ? content : [{ type: "paragraph" }] };
				}
				// Fallback: treat as HTML string
				return value;
			}
		},
		updateViewport() {
			try {
				const prev = this.isSmallScreen;
				this.isSmallScreen = window.innerWidth < 640;
				if (prev && !this.isSmallScreen) {
					const mm = this.$refs.moreMenuRef as any;
					if (mm?.isModalOpen?.()) {
						this.resizingUI = true;
						mm?.closeModal?.();
						setTimeout(() => {
							const ld = this.$refs.linkDropdown as any;
							ld?.open?.();
							this.resizingUI = false;
						}, 0);
					}
				}
				if (!prev && this.isSmallScreen) {
					if (this.isLinkDropdownOpen) {
						this.resizingUI = true;
						const ld = this.$refs.linkDropdown as any;
						ld?.close?.();
						setTimeout(() => {
							const mm = this.$refs.moreMenuRef as any;
							mm?.openFromSelection?.();
							this.resizingUI = false;
						}, 0);
					}
				}
			} catch {}
		},
		updateToolbarState() {
			if (!this.editor) return;
			const e = this.editor;
			this.activeFormats.bold = e.isActive("bold");
			this.activeFormats.italic = e.isActive("italic");
			this.activeFormats.underline = e.isActive("underline");
			this.activeFormats.bulletList = e.isActive("bulletList");
			this.activeFormats.orderedList = e.isActive("orderedList");
			let linkActive = e.isActive("link");
			if (!linkActive) {
				const sel = e.state.selection as any;
				if (sel && sel.empty) {
					const { from } = sel;
					const markType = e.state.schema.marks.link;
					const $at = e.state.doc.resolve(from);
					let range = getMarkRange($at, markType) as any;
					if (!range && from > 1) {
						const $prev = e.state.doc.resolve(Math.max(1, from - 1));
						range = getMarkRange($prev, markType) as any;
					}
					if (!range) {
						const $next = e.state.doc.resolve(from + 1);
						range = getMarkRange($next, markType) as any;
					}
					if (range && (range.to === from || range.from === from)) {
						linkActive = true;
					}
				}
			}
			this.activeFormats.link = linkActive;
			const tsAttrs = (e.getAttributes("textStyle") as Record<string, unknown>) || {};
			const tsColor = (tsAttrs.color as string) || "";
			this.activeColor = tsColor || "#000000";
			const hlAttrs = (e.getAttributes("highlight") as Record<string, unknown>) || {};
			const hlColor = (hlAttrs.color as string) || "";
			this.activeHighlight = hlColor || null;
		},
		openLinkDialog() {
			if (!this.editor) return;
			if (this.isSmallScreen) {
				const mm = this.$refs.moreMenuRef as any;
				mm?.openFromSelection?.();
				return;
			}
			const e = this.editor;
			const { from, to } = e.state.selection;
			this.pendingSelection = { from, to };
			let selectedText = from !== to ? e.state.doc.textBetween(from, to, " ") : "";
			if (!selectedText && e.isActive("link")) {
				const $pos = e.state.doc.resolve(from);
				const markType = e.state.schema.marks.link;
				const range = getMarkRange($pos, markType);
				if (range) {
					this.pendingSelection = { from: range.from, to: range.to };
					selectedText = e.state.doc.textBetween(range.from, range.to, " ");
				}
			}
			const prevHref = e.getAttributes("link")?.href as string | undefined;
			this.linkUrl = prevHref || "";
			this.linkTitle = selectedText || (prevHref ? stripProtocol(prevHref) : "");
			this.linkInfoHref = prevHref || "";
			this.linkMode = prevHref ? "edit" : "create";
			nextTick(() => {
				const ld = this.$refs.linkDropdown as any;
				ld?.open?.();
				this.isLinkDropdownOpen = true;
			});
		},
		submitLink() {
			if (!this.editor) return;
			const e = this.editor;
			const href = normalizeUrl(this.linkUrl);
			if (!href) {
				this.pendingSelection = null;
				return;
			}
			let from: number;
			let to: number;
			if (this.pendingSelection) {
				from = this.pendingSelection.from;
				to = this.pendingSelection.to;
				e.chain().setTextSelection({ from, to }).run();
			} else {
				({ from, to } = e.state.selection);
			}
			let hasSelection = from !== to;
			if (!hasSelection && e.isActive("link")) {
				const $pos = e.state.doc.resolve(from);
				const markType = e.state.schema.marks.link;
				const range = getMarkRange($pos, markType);
				if (range) {
					e.chain().setTextSelection(range).run();
					from = range.from;
					to = range.to;
					hasSelection = true;
				}
			}
			const selectedText = hasSelection ? e.state.doc.textBetween(from, to, " ") : "";
			const linkAttrs = { href };
			if (hasSelection) {
				const desiredText = (this.linkTitle || selectedText).trim();
				if (desiredText && desiredText !== selectedText) {
					e.chain()
						.focus()
						.deleteSelection()
						.insertContent({ type: "text", text: desiredText, marks: [{ type: "link", attrs: linkAttrs }] })
						.run();
					moveCaretOutOfLink(e);
				} else {
					e.chain().focus().extendMarkRange("link").setLink(linkAttrs).run();
					moveCaretOutOfLink(e);
				}
			} else {
				const display = (this.linkTitle && this.linkTitle.trim()) || stripProtocol(href);
				e.chain()
					.focus()
					.insertContent({ type: "text", text: display, marks: [{ type: "link", attrs: linkAttrs }] })
					.run();
				moveCaretOutOfLink(e);
			}
			this.pendingSelection = null;
			setTimeout(() => e.commands.focus(), 0);
		},
		cancelLink() {
			this.pendingSelection = null;
		},
		onLinkDropdownOpen() {
			this.isLinkDropdownOpen = true;
		},
		onLinkDropdownClose() {
			this.isLinkDropdownOpen = false;
			if (!this.resizingUI) {
				this.pendingSelection = null;
			}
		},
		openLinkExternally() {
			const raw = (this.linkUrl && this.linkUrl.trim()) || this.linkInfoHref;
			if (!raw) return;
			window.open(raw, "_blank", "noopener");
		},
	},
	mounted() {
		this.updateViewport();
		window.addEventListener("resize", this.updateViewport as any, { passive: true } as any);
		const self = this;
		this.editor = new Editor({
			extensions: [
				StarterKit.configure({ bulletList: {}, orderedList: {}, listItem: {}, underline: false, link: false }),
				Underline,
				TextStyle,
				Color,
				Highlight.configure({ multicolor: true }),
				CustomLink.configure({
					openOnClick: false,
					autolink: true,
					linkOnPaste: true,
					defaultProtocol: "https",
				}),
				Placeholder.configure({
					placeholder: this.placeholder || "",
					showOnlyWhenEditable: true,
					includeChildren: true,
					emptyEditorClass: "is-editor-empty",
				}),
			],
			content: this.parseModelValue(this.modelValue),
			editable: !this.readOnly,
			editorProps: {
				handleDOMEvents: {
					mousedown: (_view, event) => {
						const anchor = (event.target as HTMLElement).closest("a");
						if (!anchor) return false;
						// editing: stop navigation early
						event.preventDefault();
						(event as any).stopPropagation?.();
						return true;
					},
					pointerdown: (_view, event) => {
						const anchor = (event.target as HTMLElement).closest("a");
						if (!anchor) return false;
						event.preventDefault();
						(event as any).stopPropagation?.();
						return true;
					},
					click: (view, event) => {
						const anchor = (event.target as HTMLElement).closest("a");
						if (!anchor) return false;
						// only navigate when in read-only mode (return false, letting the event propagate further)
						if (self.readOnly) return false;
						// don't navigate immediately; instead, select the link and show the link editing UI
						event.preventDefault();
						(event as any).stopPropagation?.();
						// Select the entire link range so multi-word links are handled as a unit
						if (self.editor) {
							const posInfo = (view as any).posAtCoords({
								left: (event as MouseEvent).clientX,
								top: (event as MouseEvent).clientY,
							});
							if (posInfo && typeof posInfo.pos === "number") {
								const $pos = view.state.doc.resolve(posInfo.pos);
								const markType = view.state.schema.marks.link;
								const range = getMarkRange($pos, markType);
								if (range) {
									self.editor.chain().setTextSelection(range).focus().run();
								}
							}
						}
						// Populate href for "Open" action and invoke the same dialog as toolbar
						self.linkInfoHref = anchor.getAttribute("href") || "";
						self.openLinkDialog();
						return true;
					},
					auxclick: (_view, event) => {
						const anchor = (event.target as HTMLElement).closest("a");
						if (!anchor) return false;
						event.preventDefault();
						(event as any).stopPropagation?.();
						return true;
					},
					touchstart: (_view, event) => {
						const anchor = (event.target as HTMLElement).closest("a");
						if (!anchor) return false;
						event.preventDefault();
						(event as any).stopPropagation?.();
						return true;
					},
				},
			},
			onCreate: () => self.updateToolbarState(),
			onUpdate: ({ editor: ed }) => {
				self.$emit("update:modelValue", JSON.stringify(ed.getJSON()));
				self.updateToolbarState();
			},
			onSelectionUpdate: () => self.updateToolbarState(),
		});
	},
	watch: {
		modelValue(value: string | null) {
			const ed = this.editor;
			if (value == null) return;
			const currentJSON = JSON.stringify(ed.getJSON());
			if (value === currentJSON) return;
			// Try JSON first, then fall back to HTML
			try {
				const parsed = JSON.parse(value);
				ed.commands.setContent(parsed, false);
				return;
			} catch {}
			// Fallback: treat as HTML
			ed.commands.setContent(value, false);
		},
	},
	beforeUnmount() {
		window.removeEventListener("resize", this.updateViewport as any);
		this.editor?.destroy();
	},
});
</script>
